import styled from 'styled-components';
import { theme } from '../theme';

// Container Components
export const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  
  @media (min-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing[6]};
  }
  
  @media (min-width: ${theme.breakpoints.lg}) {
    padding: 0 ${theme.spacing[8]};
  }
`;

export const Card = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.base};
  padding: ${theme.spacing[6]};
  border: 1px solid ${theme.colors.border.light};
  
  &:hover {
    box-shadow: ${theme.shadows.md};
    transition: box-shadow ${theme.transitions.fast};
  }
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border.light};
`;

export const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

export const Grid = styled.div<{ columns?: number; gap?: string }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 1}, 1fr);
  gap: ${props => props.gap || theme.spacing[6]};
  
  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(${props => Math.min(props.columns || 1, 2)}, 1fr);
  }
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(${props => Math.min(props.columns || 1, 3)}, 1fr);
  }
  
  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(${props => props.columns || 1}, 1fr);
  }
`;

export const Flex = styled.div<{
  direction?: 'row' | 'column';
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around';
  align?: 'flex-start' | 'flex-end' | 'center' | 'stretch';
  gap?: string;
  wrap?: boolean;
}>`
  display: flex;
  flex-direction: ${props => props.direction || 'row'};
  justify-content: ${props => props.justify || 'flex-start'};
  align-items: ${props => props.align || 'stretch'};
  gap: ${props => props.gap || '0'};
  flex-wrap: ${props => props.wrap ? 'wrap' : 'nowrap'};
`;

// Typography Components
export const Heading = styled.h2<{ size?: 'sm' | 'md' | 'lg' | 'xl' }>`
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return theme.typography.fontSize.lg;
      case 'md': return theme.typography.fontSize.xl;
      case 'lg': return theme.typography.fontSize['2xl'];
      case 'xl': return theme.typography.fontSize['3xl'];
      default: return theme.typography.fontSize.xl;
    }
  }};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing[4]} 0;
`;

export const Text = styled.p<{ 
  size?: 'xs' | 'sm' | 'base' | 'lg';
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
  color?: 'primary' | 'secondary' | 'tertiary';
}>`
  font-size: ${props => theme.typography.fontSize[props.size || 'base']};
  font-weight: ${props => theme.typography.fontWeight[props.weight || 'normal']};
  color: ${props => {
    switch (props.color) {
      case 'primary': return theme.colors.text.primary;
      case 'secondary': return theme.colors.text.secondary;
      case 'tertiary': return theme.colors.text.tertiary;
      default: return theme.colors.text.primary;
    }
  }};
  margin: 0;
  line-height: ${theme.typography.lineHeight.normal};
`;

// Status and Badge Components
export const Badge = styled.span<{
  variant?: 'primary' | 'success' | 'warning' | 'error' | 'gray';
  size?: 'sm' | 'md' | 'lg';
}>`
  display: inline-flex;
  align-items: center;
  padding: ${props => {
    switch (props.size) {
      case 'sm': return `${theme.spacing[1]} ${theme.spacing[2]}`;
      case 'lg': return `${theme.spacing[3]} ${theme.spacing[4]}`;
      default: return `${theme.spacing[2]} ${theme.spacing[3]}`;
    }
  }};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return theme.typography.fontSize.xs;
      case 'lg': return theme.typography.fontSize.base;
      default: return theme.typography.fontSize.sm;
    }
  }};
  font-weight: ${theme.typography.fontWeight.medium};
  border-radius: ${theme.borderRadius.full};
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[800]};
        `;
      case 'success':
        return `
          background-color: ${theme.colors.success[100]};
          color: ${theme.colors.success[800]};
        `;
      case 'warning':
        return `
          background-color: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[800]};
        `;
      case 'error':
        return `
          background-color: ${theme.colors.error[100]};
          color: ${theme.colors.error[800]};
        `;
      default:
        return `
          background-color: ${theme.colors.gray[100]};
          color: ${theme.colors.gray[800]};
        `;
    }
  }}
`;

// Loading Components
export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${theme.colors.gray[200]};
  border-top: 2px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing[12]};
`;

// Empty State Components
export const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]};
  color: ${theme.colors.text.secondary};
`;

export const EmptyStateIcon = styled.div`
  font-size: ${theme.typography.fontSize['4xl']};
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

export const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

// Utility Components
export const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${theme.colors.border.light};
  margin: ${theme.spacing[6]} 0;
`;

export const Spacer = styled.div<{ size?: keyof typeof theme.spacing }>`
  height: ${props => theme.spacing[props.size || 4]};
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
